import { Control, Controller, FieldValue } from "react-hook-form";
import { Box, FormHelperText, MenuItem, SelectProps } from "@mui/material";

import Select from '../select';
import Checkbox from '../../checkbox/checkbox';
import Typography from '../../typography/typography';
import Chip from '../../chip/chip';
import MoreIcon from '../../icons/more';
import { MenuItemProps } from '@mui/material';
import { ReactElement } from 'react';
import ChipOrPlaceholder from './chip-or-placeholder';
import './controller-select.scss';

interface IControllerSelect {
  name: string;
  label: string;
  options: any;
  placeholder: string;
  formControl: Control<FieldValue<any>, any>;
  fieldArrayName?: string;
  position?: string;
  required?: boolean;
  size?: SelectProps["size"];
  multiple?: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
  handleSearch?: () => void;
  classes?: string;
  with_checkboxes?: boolean;
  onChipRemove?: () => void;
}

export interface IOption {
	text: string;
	value: string | number;
	isGroup?: boolean;
	parent?: string;
	options?: { text: string; value: string }[];
}

interface IMenuItems extends MenuItemProps {
	options: IOption[];
	multiple: boolean;
	placeholder: string;
	with_checkboxes: boolean;
  onChange: (value: string[]) => void
	values: string | string[];
}

const SelectedItems = ({
	data,
	options
}: {
	data: string[];
	options: IOption[];
}) => {
	const displayTitle = data.map(
		(selectedItem) =>
			options.find((option: any) => option.value === selectedItem)?.text ||
			selectedItem
	);

	return (
		<Typography
			type='s4'
			color='inherit'
			sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
			{displayTitle.slice(0.4).join(', ')}
			{data.length > 4 && (
				<Chip variant='outlined' type='normal' label={<MoreIcon />} />
			)}
		</Typography>
	);
};

const generateMenuItems = ({
	options,
	multiple,
	placeholder,
	with_checkboxes,
  onChange,
	values
}: IMenuItems) => {
	const items: ReactElement[] = [];
	if (!multiple) {
		items.push(
			<MenuItem disabled value=''>
				<em>{placeholder}</em>
			</MenuItem>
		);
	}

  const handleParentChange = (parentValue: string, childOptions: IOption[]) => {
    const childValues = childOptions.map((child) => child.value);

    const isParentSelected = values.includes(parentValue);
    const updatedValues = isParentSelected
      ? values.filter((value) => value !== parentValue && !childValues.includes(value))
      : [...values, parentValue, ...childValues];

    onChange(updatedValues);
  };

	options.forEach((option) => {
		items.push(
			<MenuItem
				value={option.value}
				className={option?.isGroup ? 'select--MenuItemHeader' : ''}>
				<Box sx={{ display: 'flex', gap: 1 }}>
					{multiple && with_checkboxes && (
						// <Checkbox checked={values?.indexOf((option.value)) > -1} />
            <Checkbox checked={values?.indexOf((option.value)) > -1}
            onChange={() => handleParentChange(option.value, option.options || [])}
          />
					)}
					<Typography type='s4' color='inherit'>
						{option.text}
					</Typography>
				</Box>
			</MenuItem>
		);
		if (option?.isGroup && Array.isArray(option?.options)) {
			option.options.forEach((opt) => {
				items.push(
					<MenuItem value={opt.value} sx={{ pl: 5 }}>
						<Box sx={{ display: 'flex', gap: 1 }}>
							{multiple && with_checkboxes && (
								<Checkbox checked={values?.indexOf(opt.value) > -1} />
							)}
							<Typography type='s4' color='inherit'>
								{opt.text}
							</Typography>
						</Box>
					</MenuItem>
				);
			});
		}
	});

	return items;
};

const ControllerSelect: React.FC<IControllerSelect> = ({
  name,
  fieldArrayName,
  options,
  label,
  placeholder,
  formControl,
  position,
  required,
  size = "small",
  multiple,
  disabled,
  searchPlaceholder,
  handleSearch,
  classes,
  with_checkboxes,
  onChipRemove,
}) => {
  return (
    <Controller
      control={formControl}
      name={`${fieldArrayName}.${name}`}
      render={({ field, fieldState: { error } }) => (
        <>
          <Select
            fullWidth
            name={name}
            dataPosition={position}
            dataName={`${fieldArrayName}.${name}`}
            placeholder={placeholder}
            label={label}
            value={field.value || (multiple ? [] : "")}
            defaultValue=""
            onChange={(e:any)=>(multiple&&e.target?.value.filter((i:any)=>i)?.length>0||!multiple&&(e.target?.value))?field.onChange(e.target.value):field.onChange("")}
            required={Boolean(required)}
            size={size}
            multiple={Boolean(multiple)}
            disabled={Boolean(disabled)}
            searchPlaceholder={searchPlaceholder}
            handleSearch={handleSearch}
            className={classes}
            variant="outlined"
            error={Boolean(error)}
            renderValue={(selected: string | string[]) => (
              <>
                {Array.isArray(selected) ? (
                  <ChipOrPlaceholder
                    data={selected}
                    placeholder={placeholder}
                    onDelete={onChipRemove}
                  />
                ) : (
                  selected || placeholder
                )}
              </>
            )}
          >
            {generateMenuItems({
              options,
              multiple: Boolean(multiple),
              placeholder: placeholder,
              values: field.value,
              with_checkboxes: Boolean(with_checkboxes),
              onChange: field.onChange
            })}
          </Select>
          {error &&
            <FormHelperText error>{error.message}</FormHelperText>
          }
          {multiple && Array.isArray(field?.value) && (
            <SelectedItems data={field.value} options={options} />
          )}
        </>
      )}
    />
  );
};

export default ControllerSelect;
